/**
 * Config Registry Types
 * Type definitions for the Config Registry UI feature
 */

// =============================================================================
// STATUS TYPES
// =============================================================================

export type ConfigVerificationStatus = 'TODO' | 'READY_FOR_QA' | 'APPROVED' | 'REJECTED';
export type ConfigPublishStatus = 'draft' | 'published';

// Backward compatibility
export type ConfigApprovalStatus = ConfigVerificationStatus;

export const CONFIG_STATUS_COLORS: Record<ConfigVerificationStatus, { bg: string; color: string }> = {
  TODO: { bg: '#e5e7eb', color: '#374151' },
  READY_FOR_QA: { bg: '#fef3c7', color: '#92400e' },
  APPROVED: { bg: '#d1fae5', color: '#065f46' },
  REJECTED: { bg: '#fee2e2', color: '#991b1b' },
};

// =============================================================================
// LOOKUP TYPES
// =============================================================================

export interface LookupValue {
  id: number;
  documentId: string;
  code: string;
  displayName: string;
  description?: string;
  parentLookupId?: string | null;
  parentValue?: string | null;
}

export interface LookupType {
  id: number;
  code: string;
  displayName: string;
  allowMultiSelect: boolean;
  isRequired: boolean;
  parentTypeCode: string | null;
  dependsOn?: string | null;
  lookups: LookupValue[];
  options?: LookupValue[]; // Alias for lookups in UI schema
}

export interface UISchemaResponse {
  lookupTypes: LookupType[];
  hierarchy: Record<string, string[]>;
}

// =============================================================================
// CONFIG RELATION TYPES
// =============================================================================

export interface ConfigRelation {
  documentId?: string;
  configType: string;
  configKey: string;
}

// =============================================================================
// CONFIG TYPES
// =============================================================================

export interface ConfigRegistryItem {
  id: number;
  documentId: string;
  configKey: string;
  configType: string;
  displayName: string;
  configDocumentation?: string;
  configDataType: string;
  defaultValue?: string | null;
  possibleValues?: any;
  validationRule?: any;

  // Workflow status fields
  verificationStatus: ConfigVerificationStatus;
  status?: ConfigPublishStatus; // Strapi's draft/published status
  approvalStatus?: ConfigApprovalStatus; // Backward compatibility

  version: number;

  // Resolved lookup fields (from GET /configs)
  component?: string[];
  section?: string | null;
  configClassification?: string;
  fileTypes?: string[];
  dataSource?: string;  // Batch config selection (S3_Pilot, SFTP_Pilot) - child of COMPONENT

  // Raw lookup data (when expand=full)
  _lookups?: { documentId: string; code: string; lookupType: string }[];

  // Config relations
  parentConfig?: ConfigRelation | null;
  relatedConfigs?: ConfigRelation[] | null;
  duplicateConfig?: ConfigRelation[] | null;
  configDependsUpon?: ConfigRelation[] | null;

  // Boolean flags
  isMandatoryField?: boolean;
  utilityConfigurationRequired?: boolean;
  deprecated?: boolean;
  specialHandlingRequired?: boolean;

  // Audit fields
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
  createdByUser?: { id: number; username: string; email: string } | null;
}

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

export interface PaginationMeta {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

/**
 * Config list response from custom config registry API
 * Note: Uses FLAT meta structure (not nested under pagination)
 * This differs from standard Strapi responses which use meta.pagination
 */
export interface ConfigListResponse {
  data: ConfigRegistryItem[];
  meta?: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
    hasMore?: boolean;
  };
}

export interface ConfigTypesResponse {
  data: string[];
}

// =============================================================================
// FILTER TYPES
// =============================================================================

export interface ConfigRegistryFilters {
  configType?: string;
  verificationStatus?: ConfigVerificationStatus | '';
  approvalStatus?: ConfigApprovalStatus | ''; // Backward compatibility
  search?: string;
  component?: string;
  section?: string;
  fileType?: string;
  classification?: string;
  restricted?: 'all' | 'yes' | 'no';
}

// =============================================================================
// FORM TYPES
// =============================================================================

export interface ConfigRegistryFormData {
  // Basic Information
  configKey: string;
  displayName: string;
  configDocumentation: string;
  
  // Typing & Classification
  configType: string;
  configDataType: string;
  selectedLookups: string[]; // documentIds
  
  // Values & Behavior
  defaultValue: string;
  possibleValues: any;
  validationRule: any;

  // Relationships
  parentConfig: ConfigRelation | null;
  relatedConfigs: ConfigRelation[];
  duplicateConfigs: ConfigRelation[];
  dependsUpon: ConfigRelation[];

  // Flags
  isMandatoryField: boolean;
  utilityConfigurationRequired: boolean;
  deprecated: boolean;
  specialHandlingRequired: boolean;
}

// =============================================================================
// REDUX STATE TYPES
// =============================================================================

export interface ConfigRegistryUIState {
  // List view state
  configs: ConfigRegistryItem[];
  pagination: PaginationMeta;
  filters: ConfigRegistryFilters;
  isLoading: boolean;
  error?: string;

  // Detail/Edit view state
  currentConfig: ConfigRegistryItem | null;
  isLoadingCurrent: boolean;

  // Lookup data (cached)
  uiSchema: UISchemaResponse | null;
  configTypes: string[];

  // UI state
  isFilterPanelOpen: boolean;
}

// =============================================================================
// API NAMESPACE TYPES
// =============================================================================

export namespace ConfigRegistryAPI {
  export interface ListParams {
    page?: number;
    pageSize?: number;
    configType?: string;
    verificationStatus?: ConfigVerificationStatus;
    approvalStatus?: ConfigApprovalStatus; // Backward compatibility
    expand?: 'full';
    // Lookup type filters (documentIds)
    component?: string;
    section?: string;
    classification?: string;
    fileType?: string;
    search?: string;
    restricted?: 'all' | 'yes' | 'no';
  }

  export interface CreatePayload {
    configType: string;
    configKey: string;
    displayName: string;
    configDataType: string;
    configDocumentation?: string;
    defaultValue?: string;
    possibleValues?: any;
    validationRule?: any;
    lookups?: string[]; // documentIds
    parentConfig?: string; // Single parent config (configType::configKey)
    relatedConfigs?: string[];
    duplicateConfig?: string[];
    configDependsUpon?: string[];
    isMandatoryField?: boolean;
    utilityConfigurationRequired?: boolean;
    deprecated?: boolean;
    specialHandlingRequired?: boolean;
  }

  export interface UpdatePayload extends Partial<CreatePayload> {
    documentId: string;
  }

  export type WorkflowAction = 'submit' | 'approve' | 'reject';
}

// =============================================================================
// TEMPLATE TYPES
// =============================================================================

/**
 * Template item - links a config to a custom value
 */
export interface TemplateItem {
  id?: number;
  configRegistryConfig: {
    documentId: string;
    configKey: string;
    configType: string;
    displayName: string;
    defaultValue?: string | null;
    configDataType?: string;
  } | null;
  value: string | null;
}

/**
 * Template - groups configs with custom values
 */
export interface Template {
  id: number;
  documentId: string;
  name: string;
  description?: string | null;
  module?: string | null;
  items: TemplateItem[];
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Template list response
 */
export interface TemplateListResponse {
  data: Template[];
  meta?: {
    pagination: PaginationMeta;
  };
}

/**
 * Template diff result
 */
export interface TemplateDiffResult {
  source: { documentId: string; name: string };
  target: { documentId: string; name: string };
  summary: {
    added: number;
    removed: number;
    modified: number;
    unchanged: number;
  };
  diff: {
    added: Array<{ documentId: string; configKey: string; configType: string; value: string }>;
    removed: Array<{ documentId: string; configKey: string; configType: string; value: string }>;
    modified: Array<{ documentId: string; configKey: string; configType: string; sourceValue: string; targetValue: string }>;
    unchanged: Array<{ documentId: string; configKey: string; configType: string; value: string }>;
  };
}

/**
 * Template update payload for adding/updating items
 */
export interface TemplateUpdatePayload {
  name?: string;
  description?: string;
  module?: string;
  items?: Array<{
    configRegistryConfig: { connect: [{ documentId: string }] } | { documentId: string };
    value: string | null;
  }>;
}

